import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronLeft, DollarSign, TrendingUp, CreditCard, Ticket } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { mockEvents } from '@/lib/mockData';
import { formatCurrency, formatNumber } from '@/lib/utils';

export function AdminEventRevenue() {
  const { eventId } = useParams();
  const event = mockEvents.find(e => e.id === eventId) || mockEvents[0];

  // Use deterministic values based on index for consistency
  const revenueByTicketType = event.ticketTypes.map((ticket, index) => ({
    name: ticket.name,
    sold: Math.floor(ticket.available * (0.3 + index * 0.2)),
    revenue: Math.floor(ticket.available * (0.3 + index * 0.2)) * ticket.price
  }));

  const totalSold = revenueByTicketType.reduce((sum, t) => sum + t.sold, 0);
  const totalRevenue = revenueByTicketType.reduce((sum, t) => sum + t.revenue, 0);

  return (
    <AdminLayout>
      <div className="p-8">
        <Link to={`/admin/events/${eventId}`} className="inline-flex items-center text-muted-foreground hover:text-white mb-6">
          <ChevronLeft className="w-5 h-5 mr-1" />
          Back to Event
        </Link>

        <h1 className="text-3xl font-bold text-white mb-2">Revenue Details</h1>
        <p className="text-muted-foreground mb-8">{event.title}</p>

        {/* Summary Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary/20 flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Revenue</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(totalRevenue)}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-green-500/20 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Net Revenue</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(totalRevenue * 0.9)}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-accent/20 flex items-center justify-center">
                <Ticket className="w-6 h-6 text-accent" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tickets Sold</p>
                <p className="text-2xl font-bold text-white">{formatNumber(totalSold)}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-card border-border p-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-blue-500/20 flex items-center justify-center">
                <CreditCard className="w-6 h-6 text-blue-500" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Avg. Order Value</p>
                <p className="text-2xl font-bold text-white">{formatCurrency(totalSold > 0 ? totalRevenue / totalSold : 0)}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Revenue by Ticket Type */}
        <Card className="bg-card border-border p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">Revenue by Ticket Type</h2>
          <div className="space-y-4">
            {revenueByTicketType.map((ticket, index) => (
              <div key={index} className="flex items-center justify-between p-4 bg-secondary/50 rounded-lg">
                <div>
                  <p className="font-semibold text-white">{ticket.name}</p>
                  <p className="text-sm text-muted-foreground">{ticket.sold} tickets sold</p>
                </div>
                <p className="text-xl font-bold text-primary">{formatCurrency(ticket.revenue)}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Fee Breakdown */}
        <Card className="bg-card border-border p-6">
          <h2 className="text-xl font-bold text-white mb-4">Fee Breakdown</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-muted-foreground">
              <span>Gross Revenue</span>
              <span className="text-white">{formatCurrency(totalRevenue)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Platform Fee (5%)</span>
              <span className="text-destructive">-{formatCurrency(totalRevenue * 0.05)}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Payment Processing (2.9% + $0.30)</span>
              <span className="text-destructive">-{formatCurrency(totalRevenue * 0.029 + (totalSold * 0.30))}</span>
            </div>
            <div className="border-t border-border pt-3 mt-3">
              <div className="flex justify-between">
                <span className="font-semibold text-white">Net Revenue</span>
                <span className="text-xl font-bold text-green-500">
                  {formatCurrency(totalRevenue - (totalRevenue * 0.079) - (totalSold * 0.30))}
                </span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
}

export default AdminEventRevenue;
